// Xavier (successor to Scholar, recycled per DIR-084 — Informed Consent)
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Search, FileText, Sparkles, ArrowLeft, Headphones } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Library Wing — Document Portal & Archives
 *
 * Sister surface to the University Wing. Browses the corpus Xavier
 * inherited when Geminus and KEYS were absorbed (Final Operating
 * Doctrine 2026-05-31).
 */
export default function LibraryPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [total, setTotal] = useState(0);

  const fetchOverview = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/library/`);
      if (r.ok) setOverview(await r.json());
    } catch {
      /* swallow */
    }
  }, []);

  const fetchDocs = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const url = new URL(`${API_URL}/api/library/documents`);
      if (q) url.searchParams.set('q', q);
      url.searchParams.set('limit', '48');
      const r = await fetch(url.toString());
      if (r.ok) {
        const data = await r.json();
        setDocs(data.documents || []);
        setTotal(data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
    fetchDocs('');
  }, [fetchOverview, fetchDocs]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchDocs(query.trim());
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="library-page">
      {/* Wing header */}
      <header className="border-b border-amber-500/30 bg-gradient-to-b from-amber-950/30 to-black">
        <div className="container mx-auto px-4 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              data-testid="library-back-home"
              className="text-amber-200/70 hover:text-amber-200 flex items-center gap-2 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              HumanCodex Gateway
            </button>
          </div>
          <nav className="flex items-center gap-2" aria-label="Knowledge Gateway wings">
            <Link
              to="/"
              data-testid="wing-switch-university"
              className="px-4 py-2 rounded-full text-sm font-mono uppercase tracking-[0.18em] border border-purple-400/50 text-purple-200 hover:bg-purple-500/10 transition-colors"
            >
              University
            </Link>
            <span
              data-testid="wing-current-library"
              className="px-4 py-2 rounded-full text-sm font-mono uppercase tracking-[0.18em] border-2 border-amber-300 bg-amber-300/10 text-amber-200"
            >
              Library
            </span>
          </nav>
        </div>

        <div className="container mx-auto px-4 pb-12">
          <div className="flex items-center gap-3 text-amber-300/70 font-mono text-xs uppercase tracking-[0.22em] mb-3">
            <BookOpen className="w-4 h-4" />
            Library Wing · Document Portal
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-3">
            The <span className="text-amber-300" style={{ textShadow: '0 0 24px rgba(252, 211, 77, 0.35)' }}>Library</span>
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl">
            {overview?.tagline || 'Open archive of NextXus Federation knowledge.'} Xavier inherited
            this collection when Geminus and KEYS were absorbed — every document is browsable,
            searchable, and free to read.
          </p>

          {overview && (
            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs font-mono">
              <span
                data-testid="library-stat-documents"
                className="px-3 py-1.5 rounded-full bg-amber-300/10 border border-amber-300/30 text-amber-200"
              >
                {overview.totals?.documents ?? 0} documents
              </span>
              <span className="px-3 py-1.5 rounded-full bg-cyan-300/10 border border-cyan-300/30 text-cyan-200">
                {overview.totals?.with_content ?? 0} with full content
              </span>
              {overview.donors?.slice(0, 4).map((d) => (
                <span
                  key={d.source_node}
                  className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300"
                >
                  {d.source_node} · {d.count}
                </span>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Search */}
      <section className="container mx-auto px-4 pt-10 pb-6">
        <form onSubmit={onSearch} className="flex gap-2 max-w-2xl" data-testid="library-search-form">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-amber-300/60" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the Library — titles, content, keywords…"
              data-testid="library-search-input"
              className="w-full pl-10 pr-4 py-3 bg-black border border-amber-500/30 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:border-amber-400"
            />
          </div>
          <button
            type="submit"
            data-testid="library-search-submit"
            className="bg-amber-400 hover:bg-amber-300 text-black font-bold px-6 rounded-lg transition-colors"
          >
            Search
          </button>
        </form>
        <p className="text-xs text-gray-500 mt-3 font-mono">
          {loading ? 'Loading documents…' : `${total} document${total === 1 ? '' : 's'} found`}
        </p>
      </section>

      {/* Doc grid */}
      <section className="container mx-auto px-4 pb-20">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-400" />
          </div>
        ) : docs.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-12 h-12 text-amber-300/40 mx-auto mb-4" />
            <p className="text-gray-400">No documents matched your search.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-doc-grid">
            {docs.map((doc) => (
              <Link
                key={doc.doc_id}
                to={`/library/${encodeURIComponent(doc.doc_id)}`}
                data-testid={`library-doc-card-${doc.doc_id}`}
                className="group bg-gray-900/50 border border-amber-500/15 hover:border-amber-300/50 rounded-xl p-5 transition-colors block"
              >
                <div className="flex items-start gap-3 mb-3">
                  <span className="shrink-0 w-9 h-9 rounded-lg bg-amber-300/10 border border-amber-300/30 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-amber-300" />
                  </span>
                  <h3 className="font-semibold text-white group-hover:text-amber-200 transition-colors line-clamp-2">
                    {doc.title}
                  </h3>
                  {doc.has_audio && (
                    <span
                      title="Audio companion available"
                      className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-400/15 border border-cyan-300/40 text-cyan-200 text-[9px] font-mono uppercase tracking-[0.16em]"
                    >
                      <Headphones className="w-3 h-3" />
                      Audio
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-400 line-clamp-3 mb-4 min-h-[3.6em]">
                  {doc.summary || <em className="text-gray-600">No preview text yet — open to see.</em>}
                </p>
                <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-gray-500">
                  <span>{doc.source_node || 'scholar'}</span>
                  <span>{doc.char_count.toLocaleString()} chars</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Footer hint */}
      <div className="border-t border-amber-500/15 bg-amber-950/10">
        <div className="container mx-auto px-4 py-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-sm text-amber-200/80">
            <Sparkles className="w-4 h-4" />
            Looking for structured learning instead? The University Wing teaches courses.
          </div>
          <Link
            to="/"
            data-testid="library-cta-university"
            className="text-sm font-mono uppercase tracking-[0.18em] text-purple-200 border border-purple-400/50 hover:bg-purple-500/10 px-4 py-2 rounded-full transition-colors"
          >
            Visit the University →
          </Link>
        </div>
      </div>
    </div>
  );
}
